import { useState, useCallback, useEffect } from "react";
import { useRecoilValue } from "recoil";
import {
    userSmartAccount,
    userSmartAccountClient,
    userSmartAccountUsdtBalance,
} from "../RecoilAtom";
import {
    fetchDepositHistory,
    fetchWithdrawalHistory,
    fetchLiquidityClaim,
    fetchPendingWithdrawals,
    fetchOpenPositions,
} from "./liquidityQueries";
import { convertFromWei, convertFromWeiEthers } from "../../utils/helpers";
import { USDR_VAULT_SHARE_DECIMALS } from "../../utils/constants";
import { useCheckAllowance } from "../../hooks/dataSender/Allowance";
import { useSessionPermissions } from "../../hooks/checkSessionPermissions";
import { getGameLiquidityContract } from "../../utils/contractHelpers";
import { LIQUIDITY_CONTRACT } from "../../utils/Enviroment";
import useCrashDataFetcher from "../../hooks/dataFetcher/useCrashDataFetcher";
import { notifyError } from "../Toast";


export const useRiskMarketLiquidity = () => {
    const smartAccount = useRecoilValue(userSmartAccount);
    const meClient = useRecoilValue(userSmartAccountClient);
    const smartAccountUsdtBalance = useRecoilValue(userSmartAccountUsdtBalance);
    const [deposittedHistory, setDeposittedHistory] = useState([]);
    const [withdrawlHistory, setWithdrawlHistory] = useState([]);
    const [totalUserWithdrawn, setTotalUserWithdrawn] = useState(0);
    const [allTimeDeposites, setAllTimeDeposites] = useState(0);
    const [currentLiquidity, setCurrentLiquidity] = useState(null);
    const [distributedRewards, setTotalDistributedRewards] = useState(null);
    const [userLiquidityShare, setUserLiquidityShare] = useState("");
    const [userLiquidityWorth, setUserLiquidityWorth] = useState(0);
    const [pendingWitdrawStatus, setPendingWithDrawStatus] = useState(false);
    const [pendingWitdrawAmount, setPendingWithDrawAmount] = useState(null);
    const [gameApy, setGameApy] = useState(0);
    const [loading, setLoading] = useState(false);

    const { grantPermissions } = useSessionPermissions();
    const {
        getMaxTrade,
        getTotalVolumn,
        AddLiqudity,
        WithDrawHook,
        userClaimRewards,
        Unreserve,
        getCrashPlatformFee,
    } = useCrashDataFetcher();

    const {
        checkAllowance,
        CurrentLiquidityYouProvide,
        TotalYourReward,
        TotalShare,
        UserReserved,
        UserPrviewUnreserve
    } = useCheckAllowance();

    const fetchDepositedHistory = useCallback(async () => {
        if (!smartAccount) return;

        try {
            const updated = await fetchDepositHistory(smartAccount);
            setDeposittedHistory(updated);
        } catch (error) {
            console.error("Failed to fetch deposit history:", error);
        }
    }, [smartAccount]);

    const fetchWithdrawldHistory = useCallback(async () => {
        if (!smartAccount) return;

        try {
            const updated = await fetchWithdrawalHistory(smartAccount);
            setWithdrawlHistory(updated);
        } catch (error) {
            console.error("Failed to fetch withdrawal history:", error);
        }
    }, [smartAccount]);

    const claimedRewardHandler = useCallback(async () => {
        if (!smartAccount) return;

        try {
            const data = await fetchLiquidityClaim(smartAccount);
            const withdrawnAmount = convertFromWei(data?.allTimeWithdraws);
            const depositsAmount = convertFromWei(data?.allTimeDeposits);

            setTotalUserWithdrawn(withdrawnAmount);
            setAllTimeDeposites(depositsAmount);
        } catch (error) {
            console.error("Failed to fetch liquidity claim:", error);
        }
    }, [smartAccount]);

    const fetchData = useCallback(async () => {
        try {
            const liquidity = await getMaxTrade(
                getGameLiquidityContract,
                LIQUIDITY_CONTRACT
            );
            const platformFee = await getCrashPlatformFee();
            setTotalDistributedRewards(platformFee);
            setCurrentLiquidity(liquidity);
        } catch (error) {
            console.error("Error fetching pool data:", error);
        }
    }, [getMaxTrade, getTotalVolumn]);

    const fetchYourData = useCallback(async () => {
        if (!smartAccount) return;

        try {
            const userCurrentLiquidity = await CurrentLiquidityYouProvide(
                getGameLiquidityContract,
                LIQUIDITY_CONTRACT,
                smartAccount
            );

            const userReward = await TotalYourReward(
                getGameLiquidityContract,
                LIQUIDITY_CONTRACT,
                smartAccount,
                userCurrentLiquidity
            );

            const formattedClaimable = convertFromWei(userReward);
            setUserLiquidityShare(userCurrentLiquidity);
            setUserLiquidityWorth(formattedClaimable);

            const userReserve = await UserReserved(
                getGameLiquidityContract,
                LIQUIDITY_CONTRACT,
                smartAccount
            );

            const reservePreview = await UserPrviewUnreserve(
                getGameLiquidityContract,
                LIQUIDITY_CONTRACT,
                smartAccount
            );

            if (userReserve?.shares) {
                setPendingWithDrawAmount(convertFromWeiEthers(userReserve.shares, USDR_VAULT_SHARE_DECIMALS));
            }

            if (reservePreview[0]) {
                setPendingWithDrawStatus(reservePreview[0]);
            }
        } catch (error) {
            console.error("Error fetching user data:", error);
        }
    }, [smartAccount, CurrentLiquidityYouProvide, TotalYourReward, UserReserved, UserPrviewUnreserve]);

    const fetchPlayerBetHistory = useCallback(async () => {
        try {
            const addd = await fetchOpenPositions();
            const total = addd.reduce((sum, item) => sum + parseInt(item.amount), 0);
            const totalSharsValue = convertFromWei(total);
            setGameApy(totalSharsValue * 0.015 * 365);
        } catch (error) {
            console.error("Failed to fetch bet history:", error);
        }
    }, []);


    const addingPendingWithdraw = useCallback(async () => {
        if (!smartAccount) return;

        try {
            const data = await fetchPendingWithdrawals(smartAccount);
            if (data.length > 0) {
                setWithdrawlHistory(prevItems => [data[0], ...prevItems]);
            }
        } catch (err) {
            console.error('Error checking pending withdraw:', err);
        }
    }, [smartAccount]);

    const handleAddLiquidity = useCallback(
        async (betAmount, setLoaderText, handleShow8, handleClose7, getAllBalances, setLiquidityAmount) => {
            if (!smartAccount || !meClient) {
                console.error("Smart account or client not available");
                return false;
            }

            setLoading(true);

            try {
                const liquidityAllowance = await checkAllowance(
                    LIQUIDITY_CONTRACT
                );
                setLoaderText({
                    heading: "Requesting wallet permissions",
                    paragraph: "Please approve the permission request in your wallet...",
                });
                const { userPermissions: permissions, userSessionKey: sessionKey } =
                    await grantPermissions();

                const needsApproval = liquidityAllowance < betAmount;
                if (needsApproval) {
                    setLoaderText({
                        heading: "Approving your transaction",
                        paragraph: "Please approve transaction for making Deposit...",
                    });
                    await AddLiqudity(
                        betAmount,
                        true,
                        smartAccount,
                        permissions,
                        sessionKey,
                        smartAccountUsdtBalance
                    );
                } else {
                    setLoaderText({
                        heading: "Processing Liquidity Addition",
                        paragraph: "Your liquidity contribution is being processed on-chain.",
                    });

                    await AddLiqudity(
                        betAmount,
                        false,
                        smartAccount,
                        permissions,
                        sessionKey,
                        smartAccountUsdtBalance
                    );
                }
                setTimeout(async () => {
                    setLoaderText({ heading: "", paragraph: "" });
                    setLoading(false);
                    handleShow8();
                    handleClose7();
                    await getAllBalances();
                    setLiquidityAmount("");
                }, 600);

                return true;
            } catch (error) {
                setTimeout(async () => {
                    setLoaderText({ heading: "", paragraph: "" });
                    setLoading(false);
                    if (error?.message?.includes("Insufficient funds to cover gas fee")) {
                        notifyError("Insufficient funds to cover gas fee.");
                    } else {
                        notifyError("Something went wrong while adding liquidity.");
                    }

                    console.error("Error adding liquidity:", error);
                }, 600);
            }

        },
        [smartAccount, meClient, checkAllowance, grantPermissions, AddLiqudity]
    );

    const handleRemoveLiquidity = useCallback(
        async (rawAmount, setLoaderText, handleShow5, handleClose4, getAllBalances, setRemoveUserLiquidity) => {
            if (!smartAccount || !meClient) return false;

            setLoading(true);
            try {
                setLoaderText({
                    heading: "Processing Liquidity Withdrawal",
                    paragraph: "Your withdrawal request is being processed on-chain.",
                });
                const allowanceUsdt = await checkAllowance(LIQUIDITY_CONTRACT);
                const isApprovalNeeded = +allowanceUsdt < +rawAmount ? true : false
                const { userPermissions: permissions, userSessionKey: sessionKey } =
                    await grantPermissions();

                await WithDrawHook(
                    rawAmount,
                    isApprovalNeeded,
                    smartAccount,
                    permissions,
                    sessionKey
                );

                setLoaderText({ heading: "", paragraph: "" });
                setRemoveUserLiquidity("");
                await getAllBalances();
                setLoading(false);
                handleShow5();
                handleClose4();

                return true;
            } catch (error) {
                setLoaderText({ heading: "", paragraph: "" });
                setLoading(false);
                if (error?.message && error.message.includes("Insufficient amount to cover gas fees")) {
                    notifyError("Insufficient amount to cover gas fees. Please top up your wallet.");
                }
                else notifyError(error?.errorMessage)
                console.error("Error removing liquidity:", error);

            }
        },
        [smartAccount, meClient, grantPermissions, WithDrawHook]
    );

    const handleClaimRewards = useCallback(
        async (setLoaderText, handleShow10, handleClose9) => {
            if (!smartAccount || !meClient) {
                console.error("Smart account or client not available");
                return false;
            }

            setLoading(true);

            try {
                setLoaderText({
                    heading: "Claiming Rewards",
                    paragraph: "Please wait while we claim your rewards...",
                });

                const { userPermissions: permissions, userSessionKey: sessionKey } =
                    await grantPermissions();

                const claimRewardsTx = await userClaimRewards(
                    smartAccount,
                    permissions,
                    sessionKey
                );

                setLoaderText({ heading: "", paragraph: "" });

                if (claimRewardsTx) {
                    setLoading(false);
                    handleShow10();
                    handleClose9();
                }

                return true;
            } catch (error) {
                setLoaderText({ heading: "", paragraph: "" });
                setLoading(false);
                console.error("Error claiming rewards:", error);
                throw error;
            }
        },
        [smartAccount, meClient, grantPermissions, userClaimRewards]
    );


    const handleUnreserve = useCallback(
        async (setLoaderText, handleClose1, handleShow2, refreshData) => {
            if (!smartAccount || !meClient) return false;
            setLoading(true);

            try {
                setLoaderText({
                    heading: "Processing Liquidity Withdrawal",
                    paragraph: "Your withdrawal request is being processed on-chain.",
                });

                const { userPermissions: permissions, userSessionKey: sessionKey } =
                    await grantPermissions();

                const claimRewardsTx = await Unreserve(
                    smartAccount,
                    permissions,
                    sessionKey
                );

                setLoaderText({ heading: "", paragraph: "" });

                if (claimRewardsTx) {
                    // OPTIMISTIC UPDATE: Clear these immediately so buttons hide before Graph refreshes
                    setPendingWithDrawAmount(null);
                    setPendingWithDrawStatus(false);

                    await refreshData();
                    handleClose1();
                    handleShow2();
                    setLoading(false);
                }
                return true;
            } catch (error) {
                setLoaderText({ heading: "", paragraph: "" });
                setLoading(false);
                console.error("Error unreserving:", error);
                throw error;
            }
        },
        [smartAccount, meClient, grantPermissions, Unreserve]
    );

    const refreshAllData = useCallback(async () => {
        await Promise.all([
            fetchData(),
            fetchDepositedHistory(),
            fetchWithdrawldHistory(),
            fetchYourData(),
            claimedRewardHandler(),
        ]);
    }, [fetchData, fetchDepositedHistory, fetchWithdrawldHistory, fetchYourData, claimedRewardHandler]);

    const formatTimestamp = useCallback((unixSeconds) => {
        const date = new Date(unixSeconds * 1000);
        const options = {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        };
        return date.toLocaleString("en-GB", options).replace(",", "");
    }, []);

    const shortenAddress = useCallback((address, chars = 4) => {
        if (!address) return "";
        return `${address?.slice(0, 2 + chars)}...${address?.slice(-chars)}`;
    }, []);

    useEffect(() => {
        if (pendingWitdrawAmount > 0) {
            addingPendingWithdraw();
        }
    }, [pendingWitdrawAmount, pendingWitdrawStatus, addingPendingWithdraw]);

    return {
        deposittedHistory,
        withdrawlHistory,
        totalUserWithdrawn,
        allTimeDeposites,
        currentLiquidity,
        distributedRewards,
        userLiquidityShare,
        userLiquidityWorth,
        pendingWitdrawStatus,
        pendingWitdrawAmount,
        gameApy,
        loading,
        smartAccountUsdtBalance,

        fetchDepositedHistory,
        fetchWithdrawldHistory,
        claimedRewardHandler,
        fetchData,
        fetchYourData,
        fetchPlayerBetHistory,
        refreshAllData,

        handleAddLiquidity,
        handleRemoveLiquidity,
        handleClaimRewards,
        handleUnreserve,

        formatTimestamp,
        shortenAddress,
        setLoading,
    };
};